import axios from "axios";
import { API_BASE_URL, API_ENDPOINTS } from "../../../../../js/api.js";
import { IAcompanhamentoStrategy } from "./IAcompanhamentoStrategy.js";
import { normalizarListaEmpresas } from "../../utils/empresa.js";
import {
  normalizarEmpresaAcompanhamentoItem,
  deduplicarAcompanhamentosPorEmpresa,
} from "../../mappers/empresaMapper.js";
import {
  extrairIdItem,
  itemCorrespondeAoTermo,
} from "../../utils/itemResolvers.js";
import { criarLogger } from "../acompanhamentoHelpers.js";

const logCiee = criarLogger("Strategy:CIEE");

const CACHE_TTL_MS = 30000;

function obterCacheEndpointCiee(url, params) {
  if (typeof window === "undefined") return null;
  if (typeof process !== "undefined" && process.env?.NODE_ENV === "test") {
    return null;
  }
  const temFiltro = params && Object.keys(params).some((k) => params[k] != null && params[k] !== "");
  if (temFiltro) return null;

  const cacheGlobal = window.__cieeValidacoesNovosCache?.[url];
  if (cacheGlobal && Date.now() - cacheGlobal.ts < CACHE_TTL_MS) {
    return Promise.resolve(cacheGlobal.data);
  }
  if (cacheGlobal?.inFlight) {
    return cacheGlobal.inFlight;
  }
  return null;
}

function buscarComCacheCiee(url, { params, headers, timeout = 8000, signal }) {
  const emCache = obterCacheEndpointCiee(url, params);
  if (emCache) return emCache;

  const temFiltro = params && Object.keys(params).some((k) => params[k] != null && params[k] !== "");
  const emTeste = typeof process !== "undefined" && process.env?.NODE_ENV === "test";

  const reqPromise = axios
    .get(url, { params, headers, timeout, signal })
    .then((resp) => {
      const data = resp?.data;
      if (!temFiltro && !emTeste && typeof window !== "undefined") {
        window.__cieeValidacoesNovosCache = window.__cieeValidacoesNovosCache || {};
        window.__cieeValidacoesNovosCache[url] = {
          ts: Date.now(),
          data,
          inFlight: null,
        };
      }
      return data;
    })
    .catch((err) => {
      if (!temFiltro && !emTeste && typeof window !== "undefined" && window.__cieeValidacoesNovosCache?.[url]) {
        window.__cieeValidacoesNovosCache[url].inFlight = null;
      }
      throw err;
    });

  if (!temFiltro && !emTeste && typeof window !== "undefined") {
    window.__cieeValidacoesNovosCache = window.__cieeValidacoesNovosCache || {};
    window.__cieeValidacoesNovosCache[url] = {
      ...(window.__cieeValidacoesNovosCache[url] || {}),
      inFlight: reqPromise,
    };
  }

  return reqPromise;
}

/**
 * Extrai imediatamente os itens já disponíveis no cache da Sidebar (/validacoes/novos)
 * para renderização instantânea (0ms) enquanto as rotas completas atualizam em background.
 */
export function obterItensCieePreCarregados() {
  if (typeof window === "undefined") return [];
  if (typeof process !== "undefined" && process.env?.NODE_ENV === "test") return [];

  if (
    window.__cieeAcompanhamentosPreloaded &&
    Date.now() - window.__cieeAcompanhamentosPreloaded.ts < CACHE_TTL_MS &&
    Array.isArray(window.__cieeAcompanhamentosPreloaded.lista) &&
    window.__cieeAcompanhamentosPreloaded.lista.length > 0
  ) {
    return window.__cieeAcompanhamentosPreloaded.lista;
  }

  const cache = window.__cieeValidacoesNovosCache;
  if (!cache) return [];

  const fontes = [
    { url: `${API_BASE_URL}/tce`, tp: 0, origem: "tce", etapas: 5 },
    { url: `${API_BASE_URL}/tce/validacoes/novos`, tp: 0, origem: "tce", etapas: 5 },
    { url: `${API_BASE_URL}/tca/validacoes/novos`, tp: 1, origem: "tca", etapas: 4 },
    { url: `${API_BASE_URL}/tca`, tp: 1, origem: "tca", etapas: 4 },
    { url: `${API_BASE_URL}/tca-empregador`, tp: 2, origem: "tca-empregador", etapas: 5 },
    { url: `${API_BASE_URL}/tca-empregador/validacoes/novos`, tp: 2, origem: "tca-empregador", etapas: 5 },
  ];

  const acumulados = [];
  for (const f of fontes) {
    const entry = cache[f.url];
    if (entry?.data) {
      const lista = normalizarListaEmpresas(entry.data);
      for (const it of lista) {
        const norm = normalizarEmpresaAcompanhamentoItem({
          ...it,
          tipo_programa: f.tp,
          _origem: f.origem,
          total_etapas: f.etapas,
          raw: {
            ...it,
            tipo_programa: f.tp,
            _origem: f.origem,
            total_etapas: f.etapas,
          },
        });
        if (norm) acumulados.push(norm);
      }
    }
  }

  return acumulados.length > 0
    ? deduplicarAcompanhamentosPorEmpresa(acumulados)
    : [];
}

/**
 * Estratégia especializada para o perfil CIEE / Administrador / Colaborador Interno.
 *
 * Princípio da Responsabilidade Única (SRP):
 * Centraliza a agregação multi-programa (TCE, TCA e TCA Empregador) e o monitoramento
 * irrestrito de todos os processos em validação, assinatura ou finalizados do ecossistema CIEEMG.
 */
export class CieeAcompanhamentoStrategy extends IAcompanhamentoStrategy {
  async carregar({ token, contexto = {}, signal } = {}) {
    const busca = contexto.busca || "";
    const empresaId = contexto.empresaId || null;

    logCiee("Carregando acompanhamentos multi-programa CIEE...", { busca, empresaId });

    const headers = { Authorization: `Bearer ${token}` };
    const params = {
      ...(empresaId ? { empresa_id: empresaId } : {}),
    };

    const endpointsProgramas = [
      { url: `${API_BASE_URL}/tce`, tp: 0, origem: "tce", etapas: 5 },
      { url: `${API_BASE_URL}/tce/validacoes/novos`, tp: 0, origem: "tce", etapas: 5 },
      { url: `${API_BASE_URL}/tca`, tp: 1, origem: "tca", etapas: 4 },
      { url: `${API_BASE_URL}/tca/validacoes/novos`, tp: 1, origem: "tca", etapas: 4 },
      { url: `${API_BASE_URL}/tca/validacoes/minhas`, tp: 1, origem: "tca", etapas: 4 },
      { url: `${API_BASE_URL}/tca-empregador`, tp: 2, origem: "tca-empregador", etapas: 5 },
      { url: `${API_BASE_URL}/tca-empregador/validacoes/novos`, tp: 2, origem: "tca-empregador", etapas: 5 },
    ];

    const mapearItemPrograma = (it, prog) =>
      normalizarEmpresaAcompanhamentoItem({
        ...it,
        tipo_programa: prog.tp,
        _origem: prog.origem,
        total_etapas: prog.etapas,
        raw: {
          ...it,
          tipo_programa: prog.tp,
          _origem: prog.origem,
          total_etapas: prog.etapas,
        },
      });

    const resultadosProgramas = await Promise.all(
      endpointsProgramas.map(async (prog) => {
        try {
          const data = await buscarComCacheCiee(prog.url, {
            params,
            headers,
            timeout: 8000,
            signal,
          });
          const lista = normalizarListaEmpresas(data);
          return lista.map((it) => mapearItemPrograma(it, prog)).filter(Boolean);
        } catch (_) {
          return [];
        }
      }),
    );

    let todosUnificados = resultadosProgramas.flat();

    const endpointMe =
      typeof API_ENDPOINTS?.acompanhamentosMe === "function"
        ? API_ENDPOINTS.acompanhamentosMe()
        : API_ENDPOINTS?.acompanhamentosMe || `${API_BASE_URL}/acompanhamentos/me`;

    // Se as rotas rápidas já trouxeram itens, /acompanhamentos/me roda em segundo plano
    // para complementar qualquer contrato adicional sem travar a abertura da tela por 5.75s.
    const buscarComplementoMe = () =>
      buscarComCacheCiee(endpointMe, {
        params: {
          limit: 500,
          ...(empresaId ? { empresa_id: empresaId } : {}),
          ...(busca ? { busca } : {}),
        },
        headers,
        timeout: 12000,
        signal,
      })
        .then((dataMe) =>
          normalizarListaEmpresas(dataMe)
            .map(normalizarEmpresaAcompanhamentoItem)
            .filter(Boolean),
        )
        .catch(() => []);

    if (todosUnificados.length === 0) {
      const itensMe = await buscarComplementoMe();
      todosUnificados = [...itensMe];
    } else if (typeof contexto.onComplemento === "function") {
      buscarComplementoMe().then((itensMe) => {
        if (!Array.isArray(itensMe) || itensMe.length === 0) return;
        let combinados = deduplicarAcompanhamentosPorEmpresa([
          ...itensMe,
          ...todosUnificados,
        ]);
        if (
          !busca &&
          !empresaId &&
          typeof window !== "undefined" &&
          !(typeof process !== "undefined" && process.env?.NODE_ENV === "test")
        ) {
          window.__cieeAcompanhamentosPreloaded = {
            ts: Date.now(),
            lista: combinados,
          };
        }
        if (busca) {
          combinados = combinados.filter((it) => itemCorrespondeAoTermo(it, busca));
        }
        contexto.onComplemento(combinados);
      });
    }

    // 3. Se houver busca por ID numérico (ex: "109", "#109"), busca diretamente nos endpoints dos 3 programas
    const termoIdLimpo = String(busca || "").replace(/\D/g, "");
    if (termoIdLimpo) {
      const jaContem = todosUnificados.some(
        (it) => String(extrairIdItem(it)) === termoIdLimpo,
      );
      if (!jaContem) {
        const endpointsId = [
          { url: `${API_BASE_URL}/tce/${termoIdLimpo}`, tp: 0, origem: "tce", etapas: 5 },
          { url: `${API_BASE_URL}/tca/${termoIdLimpo}`, tp: 1, origem: "tca", etapas: 4 },
          { url: `${API_BASE_URL}/tca-empregador/${termoIdLimpo}`, tp: 2, origem: "tca-empregador", etapas: 5 },
        ];
        const resId = await Promise.allSettled(
          endpointsId.map(async (ep) => {
            try {
              const resp = await axios.get(ep.url, {
                headers: { Authorization: `Bearer ${token}` },
                timeout: 10000,
                signal,
              });
              if (
                resp?.data &&
                (resp.data.id ||
                  resp.data.tce_id ||
                  resp.data.tca_id ||
                  resp.data.autorizacao_id)
              ) {
                return normalizarEmpresaAcompanhamentoItem({
                  ...resp.data,
                  id: resp.data.id || termoIdLimpo,
                  tipo_programa: resp.data.tipo_programa ?? ep.tp,
                  _origem: resp.data._origem || ep.origem,
                  total_etapas: ep.etapas,
                  raw: {
                    ...resp.data,
                    tipo_programa: resp.data.tipo_programa ?? ep.tp,
                    _origem: resp.data._origem || ep.origem,
                    total_etapas: ep.etapas,
                  },
                });
              }
            } catch (_) {}
            return null;
          }),
        );

        for (const r of resId) {
          if (r.status === "fulfilled" && r.value) {
            todosUnificados.unshift(r.value);
          }
        }
      }
    }

    // 4. Deduplica por chave composta origem + id
    let listaFinal = deduplicarAcompanhamentosPorEmpresa(todosUnificados);

    if (
      !busca &&
      !empresaId &&
      typeof window !== "undefined" &&
      !(typeof process !== "undefined" && process.env?.NODE_ENV === "test") &&
      listaFinal.length > 0
    ) {
      window.__cieeAcompanhamentosPreloaded = {
        ts: Date.now(),
        lista: listaFinal,
      };
    }

    // 5. Aplica filtro de busca de forma abrangente se informado
    if (busca) {
      listaFinal = listaFinal.filter((it) => itemCorrespondeAoTermo(it, busca));
    }

    logCiee("Total de itens consolidados para CIEE:", listaFinal.length);
    return listaFinal;
  }
}
